import {StreamChat, Channel, Event as StreamEvent} from "stream-chat";
import {debugLog} from "../utils/debug";

export interface ChatMessage {
  text?: string;
  notification?: any;
  [key: string]: any;
}

export interface ChatOptions {
  serverUrl: string;
}

export interface ChatHandle {
  init(data: {
    userId: string;
    userName?: string;
    channelId: string;
    channelName?: string;
  }): Promise<void>;
  sendMessage(message: ChatMessage): Promise<any>;
  sendNotification(data: {
    text: string;
    participants: string[];
    sender: {deviceId: string; name?: string; img?: string};
  }): Promise<any>;
  leave(): Promise<void>;
  getChannel(): Channel | null;
}

export function createChatManager(options: ChatOptions): ChatHandle {
  const {serverUrl} = options;
  let client: StreamChat | null = null;
  let activeChannel: Channel | null = null;
  let onMessageCallback: ((message: any) => void) | null = null;

  function setOnMessage(cb: (message: any) => void) {
    onMessageCallback = cb;
  }

  function handleNewMessage(event: StreamEvent) {
    const message = event.message;
    if (message && onMessageCallback) {
      onMessageCallback(message);
    }
  }

  async function fetchStreamKey(userId: string): Promise<string> {
    const response = await fetch(
      `${serverUrl}/api/chat/stream-key?userId=${userId}`,
      {
        method: "GET",
        headers: {"Content-Type": "application/json"},
      }
    );
    if (!response.ok) {
      throw new Error(`Failed to get stream key: HTTP ${response.status}`);
    }
    const data = await response.json();
    if (!data.success || !data.consumerKey) {
      throw new Error("Stream key not available");
    }
    return data.consumerKey;
  }

  async function fetchStreamToken(userId: string): Promise<string> {
    const response = await fetch(`${serverUrl}/api/chat/stream-token`, {
      method: "POST",
      headers: {"Content-Type": "application/json"},
      body: JSON.stringify({userId}),
    });
    if (!response.ok) {
      throw new Error(`Failed to get stream token: HTTP ${response.status}`);
    }
    const data = await response.json();
    if (!data.success || !data.token) {
      throw new Error("Stream token not available");
    }
    return data.token;
  }

  async function init(data: {
    userId: string;
    userName?: string;
    channelId: string;
    channelName?: string;
  }): Promise<void> {
    const {userId, userName, channelId, channelName} = data;
    const key = await fetchStreamKey(userId);
    client = StreamChat.getInstance(key);

    const token = await fetchStreamToken(userId);

    debugLog("Chat", `Connecting user ${userId}...`);
    const user = await client.connectUser(
      {id: userId, name: userName || userId},
      token
    );
    if (!user) throw new Error("Could not connect chat user");
    debugLog("Chat", "Connected successfully");

    const channel = client.channel("livestream", channelId, {
      name: channelName || channelId,
    });
    await channel.watch();
    channel.on("message.new", handleNewMessage);
    activeChannel = channel;
  }

  async function sendMessage(message: ChatMessage): Promise<any> {
    if (!activeChannel) throw new Error("Chat not initialized");
    return activeChannel.sendMessage(message);
  }

  async function sendNotification(data: {
    text: string;
    participants: string[];
    sender: {deviceId: string; name?: string; img?: string};
  }): Promise<any> {
    return sendMessage({
      notification: {
        isNotification: true,
        text: data.text,
        participants: data.participants,
        sender: data.sender,
      },
    });
  }

  async function leave(): Promise<void> {
    if (activeChannel) {
      activeChannel.off("message.new", handleNewMessage);
      await activeChannel.stopWatching();
    }
    if (client) {
      await client.disconnectUser();
    }
    activeChannel = null;
    client = null;
  }

  function getChannel(): Channel | null {
    return activeChannel;
  }

  return {
    init,
    sendMessage,
    sendNotification,
    leave,
    getChannel,
    // Expose for SDK event wiring
    set onMessage(cb: (message: any) => void) {
      onMessageCallback = cb;
    },
  } as ChatHandle & {onMessage: (message: any) => void};
}
